import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Send, Paperclip, MoreVertical, CheckCircle,
  Clock, User, HeadphonesIcon, Mail, XCircle, Copy, RefreshCw, Loader
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContextMySQL';
import { authHeaders, orderTokenFor, API_URL } from '../api';
import { mergeMessages, isNearBottom, scrollChatToEnd, formatTime } from '../components/chat/chatShared';
import ConfirmModal from '../components/chat/ConfirmModal';
import { EmojiButton, Lightbox, Attachment, NewMessagesPill, SendStatus } from '../components/chat/ChatParts';

const NODE_API = `${API_URL}/api`;
const NODE_BASE = API_URL;
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'];
const MAX_FILE_MB = 10;

const postSupportMessage = (orderId, sender, text, clientId, file = null, orderToken = null) =>
  fetch(`${NODE_API}/support_messages`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      orderId,
      sender,
      text,
      clientId,
      clientTs: new Date().toISOString(),
      ...(orderToken ? { token: orderToken } : {}),
      ...(file ? { fileData: file.data, fileName: file.name, fileType: file.type } : {})
    })
  }).then(r => r.ok).catch(() => false);

const setMsgStatus = (setMessages, id, status) =>
  setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));

export default function CustomerServiceChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const chatRef = useRef(null);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);
  const nearBottomRef = useRef(true);
  const autoScrollRef = useRef(false);
  const sentByMe = useRef(new Set());

  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      sender: 'support',
      text: 'Bonjour ! Bienvenue sur le service client AVKAMAS. Comment pouvons-nous vous aider aujourd\'hui ?',
      timestamp: new Date(Date.now() - 300000).toISOString(),
      status: 'read'
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [newBelowCount, setNewBelowCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Suivi de la position de scroll de l'utilisateur.
  // Les positions intermédiaires d'un scroll programmatique (smooth) sont
  // ignorées pour ne pas fausser nearBottomRef ; un geste utilisateur
  // (molette/toucher) annule immédiatement le scroll automatique en cours.
  const handleScroll = () => {
    const el = chatRef.current;
    if (!el) return;
    const near = isNearBottom(el);
    if (autoScrollRef.current) {
      if (near) autoScrollRef.current = false;
      return;
    }
    nearBottomRef.current = near;
    if (near) setNewBelowCount(0);
  };

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    const cancel = () => { autoScrollRef.current = false; };
    el.addEventListener('wheel', cancel, { passive: true });
    el.addEventListener('touchstart', cancel, { passive: true });
    return () => {
      el.removeEventListener('wheel', cancel);
      el.removeEventListener('touchstart', cancel);
    };
  }, []);

  // Auto-scroll intelligent : seulement si l'utilisateur est déjà en bas,
  // ou vient d'envoyer un message ; sinon pastille "nouveaux messages".
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last && sentByMe.current.has(String(last.id))) {
      sentByMe.current.delete(String(last.id));
      nearBottomRef.current = true;
      scrollChatToEnd(chatRef, true, autoScrollRef);
      return;
    }
    if (nearBottomRef.current) {
      scrollChatToEnd(chatRef, true, autoScrollRef);
    } else {
      setNewBelowCount(c => c + 1);
    }
  }, [messages]);

  useEffect(() => {
    const selectedChat = location.state?.chatHistory ||
      JSON.parse(localStorage.getItem('selectedChat') || 'null');

    if (selectedChat && selectedChat.messages) {
      setMessages(selectedChat.messages);
      setOrderData(selectedChat.orderData);
      nearBottomRef.current = true;
      scrollChatToEnd(chatRef, false);
      return;
    }

    const completedOrder = location.state?.orderData ||
      JSON.parse(localStorage.getItem('completedOrder') || 'null');

    if (completedOrder) {
      setOrderData(completedOrder);
      const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
      const existingChat = chatHistory.find(c => c.orderData?.id === completedOrder.id);

      if (existingChat) {
        setMessages(existingChat.messages || []);
      } else {
        const orderMessage = {
          id: `order-${completedOrder.id}-details`,
          sender: 'system',
          text: `📋 Détails de la commande #${completedOrder.id || 'N/A'}:
${completedOrder.type === 'buy' ? '🛒 ACHAT' : '💰 VENTE'} de ${completedOrder.quantity}M Kamas
🎮 Serveur: ${completedOrder.server} (${completedOrder.category})
👤 Personnage: ${completedOrder.character}
💰 Prix: ${completedOrder.totalPrice?.toFixed(2)} DH (${completedOrder.pricePerM?.toFixed(2)} DH/M)
📧 Email: ${completedOrder.email}
${completedOrder.phone ? `📱 Téléphone: ${completedOrder.phone}` : ''}
${completedOrder.type === 'sell' ? `🏦 Banque: ${completedOrder.bankName}\n👤 Titulaire: ${completedOrder.accountHolder}\n💳 RIB: ${completedOrder.accountNumber}` : ''}`,
          timestamp: new Date().toISOString(),
          status: 'read'
        };
        setMessages(prev => [...prev, orderMessage]);
        chatHistory.push({
          id: Date.now().toString(),
          orderData: completedOrder,
          messages: [orderMessage],
          createdAt: new Date().toISOString(),
          status: 'unread'
        });
        localStorage.setItem('chatHistory', JSON.stringify(chatHistory));
      }

      // Synchronise le récap de commande vers le serveur (INSERT IGNORE côté API)
      postSupportMessage(
        completedOrder.id,
        'system',
        `📋 Détails de la commande #${completedOrder.id || 'N/A'}:
${completedOrder.type === 'buy' ? '🛒 ACHAT' : '💰 VENTE'} de ${completedOrder.quantity}M Kamas
🎮 Serveur: ${completedOrder.server} (${completedOrder.category})
👤 Personnage: ${completedOrder.character}
💰 Prix: ${completedOrder.totalPrice?.toFixed(2)} DH (${completedOrder.pricePerM?.toFixed(2)} DH/M)
📧 Email: ${completedOrder.email}
${completedOrder.phone ? `📱 Téléphone: ${completedOrder.phone}` : ''}`,
        `order-${completedOrder.id}-details`,
        null,
        orderTokenFor(completedOrder.id, completedOrder)
      );
    }
    nearBottomRef.current = true;
    scrollChatToEnd(chatRef, false);
  }, [location.state]);

  // Réponses admin : polling serveur + évènement localStorage (autre onglet)
  useEffect(() => {
    if (!orderData?.id) return;

    const checkForAdminMessages = async () => {
      const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
      const currentChat = chatHistory.find(c => c.orderData?.id === orderData.id);

      if (currentChat?.messages) {
        setMessages(prev => mergeMessages(prev, currentChat.messages));
        if (currentChat.orderData?.status === 'cancelled' && orderData.status !== 'cancelled') {
          setOrderData(prev => ({ ...prev, status: 'cancelled' }));
        }
      }

      const orderToken = orderTokenFor(orderData.id, orderData);

      try {
        const res = await fetch(
          `${NODE_API}/support_messages?orderId=${orderData.id}${orderToken ? `&token=${orderToken}` : ''}`,
          { headers: authHeaders() }
        );
        const data = await res.json();
        if (data.success && data.messages?.length) {
          const serverMsgs = data.messages.map(m => ({
            id: m.client_id || `srv-${m.id}`,
            sender: m.sender,
            text: m.text,
            timestamp: m.client_ts || m.created_at,
            fileUrl: m.file_url ? `${NODE_BASE}${m.file_url}` : null,
            fileName: m.file_name,
            fileType: m.file_type,
            status: 'read'
          }));
          setMessages(prev => mergeMessages(prev, serverMsgs));
        }
      } catch (e) { /* serveur indisponible */ }

      try {
        const res = await fetch(
          `${NODE_API}/order_status?orderId=${orderData.id}${orderToken ? `&token=${orderToken}` : ''}`,
          { headers: authHeaders() }
        );
        const data = await res.json();
        if (data.success && data.status && data.status !== orderData.status) {
          setOrderData(prev => ({ ...prev, status: data.status }));
        }
      } catch (e) { /* serveur indisponible */ }
    };

    checkForAdminMessages();
    const interval = setInterval(checkForAdminMessages, 2000);
    const handleStorageChange = (e) => { if (e.key === 'chatHistory') checkForAdminMessages(); };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [orderData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fermeture du menu ⋮ au clic extérieur / Échap
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const requireAuth = () => {
    if (isAuthenticated) return true;
    navigate('/login');
    return false;
  };

  const sendMessage = async (text, file = null, localMessage = null) => {
    const clientId = localMessage?.id || `u-${orderData.id}-${Date.now()}`;
    const ok = await postSupportMessage(
      orderData.id, 'user', text, clientId, file, orderTokenFor(orderData.id, orderData)
    );
    setMsgStatus(setMessages, clientId, ok ? 'sent' : 'error');
    return ok;
  };

  const retryMessage = (message) => {
    setMsgStatus(setMessages, message.id, 'sending');
    postSupportMessage(
      orderData.id, 'user', message.text, message.id,
      message.fileData ? { data: message.fileData, name: message.fileName, type: message.fileType } : null,
      orderTokenFor(orderData.id, orderData)
    ).then(ok => setMsgStatus(setMessages, message.id, ok ? 'sent' : 'error'));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || !requireAuth()) return;
    if (!orderData?.id) return;

    const clientId = `u-${orderData.id}-${Date.now()}`;
    const userMessage = {
      id: clientId,
      sender: 'user',
      text,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };
    sentByMe.current.add(clientId);
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');

    const ok = await sendMessage(text, null, userMessage);
    if (!ok) return;

    const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
    localStorage.setItem('chatHistory', JSON.stringify(chatHistory.map(c =>
      c.orderData?.id === orderData.id
        ? { ...c, messages: [...(c.messages || []), { ...userMessage, status: 'sent' }], status: 'unread' }
        : c
    )));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !requireAuth()) return;

    if (!orderData?.id) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      alert(`Fichier trop volumineux (max ${MAX_FILE_MB} Mo)`);
      return;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      alert('Format non accepté : PNG, JPG, WEBP, GIF ou PDF uniquement.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const clientId = `u-${orderData.id}-${Date.now()}`;
      const caption = newMessage.trim();
      const fileMessage = {
        id: clientId,
        sender: 'user',
        text: caption,
        fileData: dataUrl,
        fileName: file.name,
        fileType: file.type,
        timestamp: new Date().toISOString(),
        status: 'sending'
      };
      sentByMe.current.add(clientId);
      setMessages(prev => [...prev, fileMessage]);
      setNewMessage('');
      setUploading(true);

      const ok = await sendMessage(caption, { data: dataUrl, name: file.name, type: file.type }, fileMessage);
      setUploading(false);
      if (!ok) return;

      const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
      localStorage.setItem('chatHistory', JSON.stringify(chatHistory.map(c =>
        c.orderData?.id === orderData.id
          ? {
              ...c,
              messages: [...(c.messages || []), {
                id: clientId,
                sender: 'user',
                text: caption || `📎 ${file.name}`,
                fileName: file.name,
                fileType: file.type,
                timestamp: fileMessage.timestamp,
                status: 'sent'
              }],
              status: 'unread'
            }
          : c
      )));
    };
    reader.readAsDataURL(file);
  };

  const doCancelOrder = () => {
    setConfirmCancel(false);
    if (!orderData?.id) return;

    const cancelMessage = {
      id: `cancel-${orderData.id}`,
      sender: 'system',
      text: '❌ Commande annulée par le client',
      timestamp: new Date().toISOString(),
      status: 'read'
    };
    sentByMe.current.add(cancelMessage.id);
    setMessages(prev => [...prev, cancelMessage]);
    setOrderData(prev => ({ ...prev, status: 'cancelled' }));

    const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
    localStorage.setItem('chatHistory', JSON.stringify(chatHistory.map(c =>
      c.orderData?.id === orderData.id
        ? {
            ...c,
            messages: [...(c.messages || []), cancelMessage],
            orderData: { ...c.orderData, status: 'cancelled' },
            status: 'unread'
          }
        : c
    )));

    const adminOrders = JSON.parse(localStorage.getItem('admin_orders') || '[]');
    localStorage.setItem('admin_orders', JSON.stringify(
      adminOrders.map(o => o.id === orderData.id ? { ...o, status: 'cancelled' } : o)
    ));

    const orderToken = orderTokenFor(orderData.id, orderData);
    fetch(`${NODE_API}/orders/cancel`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ orderId: orderData.id, ...(orderToken ? { token: orderToken } : {}) })
    }).catch(() => {});
    postSupportMessage(orderData.id, 'system', '❌ Commande annulée par le client', `cancel-${orderData.id}`, null, orderToken);
  };

  const copyOrderId = () => {
    if (!orderData?.id) return;
    navigator.clipboard?.writeText(orderData.id).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    setMenuOpen(false);
  };

  const isCancelled = orderData?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900 border-b border-gray-800 sticky top-16 lg:top-20 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-2 h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-1.5 flex-shrink-0">
              <ArrowLeft size={18} className="text-gray-400" />
              <span className="text-sm sm:text-base text-gray-400 hover:text-white">Retour</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                <HeadphonesIcon size={18} className="text-black" />
              </div>
              <div className="min-w-0">
                <h1 className="font-bold text-sm sm:text-base leading-tight">Service Client AVKAMAS</h1>
                <div className="flex items-center gap-1 text-xs text-green-400">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  En ligne
                </div>
              </div>
            </div>
            <div className="relative flex-shrink-0" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Options de la conversation"
                aria-expanded={menuOpen}
                className="p-2 -mr-2 hover:bg-gray-800 rounded-lg"
              >
                <MoreVertical size={20} className="text-gray-400" />
              </button>
              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    className="absolute right-0 top-10 z-40 w-56 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                  >
                    <button
                      onClick={copyOrderId}
                      disabled={!orderData?.id}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-left hover:bg-gray-700 transition-colors disabled:opacity-40"
                    >
                      <Copy size={15} className="text-gray-400" />
                      {copied ? 'Copié !' : 'Copier le n° de commande'}
                    </button>
                    <button
                      onClick={() => { setMenuOpen(false); window.location.reload(); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-left hover:bg-gray-700 transition-colors"
                    >
                      <RefreshCw size={15} className="text-gray-400" />
                      Actualiser la conversation
                    </button>
                    {orderData && !isCancelled && (
                      <button
                        onClick={() => { setMenuOpen(false); setConfirmCancel(true); }}
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-left text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <XCircle size={15} />
                        Annuler la commande
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-6">
        {orderData && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-900/80 border border-gray-800 rounded-xl p-3 sm:p-4 mb-3 sm:mb-6"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <CheckCircle className={`w-5 h-5 flex-shrink-0 ${isCancelled ? 'text-red-400' : 'text-green-400'}`} />
                <div className="min-w-0">
                  <p className="font-medium text-sm sm:text-base truncate">Commande #{orderData.id || 'N/A'}</p>
                  <p className="text-xs sm:text-sm text-gray-400 truncate">
                    {orderData.type === 'buy' ? 'Achat' : 'Vente'} de {orderData.quantity}M Kamas - {orderData.server}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-yellow-400 text-sm sm:text-base">{orderData.totalPrice?.toFixed(2)} DH</p>
                <p className={`text-xs capitalize ${isCancelled ? 'text-red-400' : 'text-gray-400'}`}>{orderData.status || 'en cours'}</p>
              </div>
            </div>

            {!isCancelled && (
              <div className="mt-3 pt-3 border-t border-gray-800">
                <button
                  onClick={() => setConfirmCancel(true)}
                  className="w-full sm:w-auto px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg text-sm hover:bg-red-500/30 transition-colors flex items-center justify-center gap-2"
                >
                  <XCircle size={16} />
                  Annuler la commande
                </button>
              </div>
            )}

            {isCancelled && (
              <div className="mt-3 pt-3 border-t border-gray-800">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm text-red-400 flex items-center gap-2">
                    <XCircle size={16} />
                    Cette commande a été annulée
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-3 sm:mb-6"
        >
          <a
            href="mailto:commande@achatventekamas.com"
            className="bg-gray-900/80 border border-gray-800 rounded-xl px-3 py-2.5 sm:p-4 hover:border-yellow-500/50 transition-colors flex items-center gap-3 sm:gap-4"
          >
            <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm sm:text-base">Email</p>
              <p className="text-xs sm:text-sm text-gray-400 truncate">commande@achatventekamas.com</p>
            </div>
          </a>
        </motion.div>

        <div className="relative">
          <div
            ref={chatRef}
            onScroll={handleScroll}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 h-[55vh] min-h-[16rem] sm:h-96 overflow-y-auto"
          >
            <div className="space-y-3 sm:space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] sm:max-w-xs lg:max-w-md break-words ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black'
                      : message.sender === 'system'
                      ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400'
                      : 'bg-gray-800 text-white'
                  } rounded-2xl px-3 sm:px-4 py-2 sm:py-3`}>
                    {message.sender === 'system' && (
                      <div className="flex items-center gap-2 mb-1 pb-2 border-b border-blue-500/30">
                        <Clock size={14} />
                        <span className="text-xs font-medium">Système</span>
                      </div>
                    )}
                    {message.sender === 'user' && (
                      <div className="flex items-center gap-2 mb-1 pb-2 border-b border-black/20">
                        <User size={14} />
                        <span className="text-xs font-medium">Vous</span>
                      </div>
                    )}
                    <Attachment
                      message={message}
                      darkText={message.sender === 'user'}
                      onPreview={(src, name) => setLightbox({ src, name })}
                    />
                    {message.text && (
                      <p className="text-xs sm:text-sm whitespace-pre-wrap">{message.text}</p>
                    )}
                    <div className={`flex items-center justify-end gap-1.5 mt-1 text-xs ${
                      message.sender === 'user' ? 'text-black/70' : 'text-gray-500'
                    }`}>
                      <span>{formatTime(message.timestamp)}</span>
                      {message.sender === 'user' && (
                        <SendStatus status={message.status} onRetry={() => retryMessage(message)} />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <NewMessagesPill
            visible={newBelowCount > 0}
            count={newBelowCount}
            onClick={() => { scrollChatToEnd(chatRef, true, autoScrollRef); setNewBelowCount(0); }}
          />
        </div>

        <form onSubmit={handleSendMessage} className="bg-gray-900/80 border border-gray-800 rounded-xl p-2 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Envoyer une image ou un PDF (preuve de paiement)"
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0 disabled:opacity-50"
            >
              {uploading
                ? <Loader size={20} className="text-yellow-400 animate-spin" />
                : <Paperclip size={20} className="text-gray-400" />}
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Écrivez votre message..."
              maxLength={4000}
              className="flex-1 min-w-0 bg-gray-950 border border-gray-700 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white text-sm focus:border-yellow-500 focus:outline-none"
            />
            <EmojiButton onPick={(emoji) => setNewMessage(prev => prev + emoji)} />
            <button
              type="submit"
              disabled={!newMessage.trim() || uploading}
              className="p-2 sm:p-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <Send size={18} />
            </button>
          </div>
        </form>

        <div className="mt-3 sm:mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 sm:p-4 text-xs sm:text-sm">
          <p className="text-sm text-yellow-400">
            <strong>Information :</strong> Notre équipe de support est disponible pour suivre votre commande en direct dans ce chat.
          </p>
        </div>
      </div>

      <ConfirmModal
        open={confirmCancel}
        danger
        title="Annuler cette commande ?"
        message={`La commande #${orderData?.id || ''} sera annulée définitivement et le support sera notifié. Cette action est irréversible.`}
        confirmLabel="Oui, annuler"
        cancelLabel="Garder la commande"
        onConfirm={doCancelOrder}
        onCancel={() => setConfirmCancel(false)}
      />
      <Lightbox src={lightbox?.src} name={lightbox?.name} onClose={() => setLightbox(null)} />
    </div>
  );
}
